import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  InternalContainer,
  ExternalContainer,
} from "../../components/Reusable/Layout";
import {
  createSetting,
  deleteSetting,
  fetchSettings,
  refreshAppData,
  updateSetting,
} from "../../features/auth/store/settingSlice";
import {
  SettingItem,
  SettingPayload,
} from "../../features/auth/store/types/setting-types";
import {
  AddMarchantModal,
  SettingsSearchFilter,
  SettingsTable,
} from "./component";

const Settings = () => {
  const dispatch = useAppDispatch();
  const items = useAppSelector((state) => state.settings?.items ?? []);
  const loading = useAppSelector((state) => state.settings?.loading ?? false);
  const saving = useAppSelector((state) => state.settings?.saving ?? false);
  const appDataRefreshing = useAppSelector(
    (state) => state.settings?.appDataRefreshing ?? false,
  );
  const error = useAppSelector((state) => state.settings?.error ?? null);
  const [isCreateMerchantModalOpen, setIsCreateMerchantModalOpen] =
    useState(false);
  const [editing, setEditing] = useState<SettingItem | null>(null);
  const [updatingSettingIds, setUpdatingSettingIds] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<SettingPayload>({
    setting: "",
    value: "",
    visible: "on" as const,
  });

  const handleCreateMerchantClick = () => {
    setEditing(null);
    setForm({
      setting: "",
      value: "",
      visible: "on",
    });
    setIsCreateMerchantModalOpen(true);
  };

  const handleCloseCreateMerchantModal = () => {
    setIsCreateMerchantModalOpen(false);
    setEditing(null);
  };

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  useEffect(() => {
    if (!editing) return;

    setForm({
      setting: editing.setting,
      value: editing.value ?? "",
      visible: editing.visible,
    });
  }, [editing]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;

    return items.filter((item) =>
      [item.setting, item.value, item.visible]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [items, search]);

  const handleEdit = (item: SettingItem) => {
    setEditing(item);
    setIsCreateMerchantModalOpen(true);
  };

  const handleDelete = async (item: SettingItem) => {
    if (!window.confirm(`Delete setting "${item.setting}"?`)) return;

    try {
      await dispatch(deleteSetting(item.id)).unwrap();
      toast.success("Setting deleted successfully");
    } catch (err: any) {
      toast.error(err || "Failed to delete setting");
    }
  };

  const handleToggleValue = async (item: SettingItem) => {
    const nextValue = item.value?.toLowerCase() === "on" ? "off" : "on";

    setUpdatingSettingIds((prev) => [...prev, item.id]);

    try {
      await dispatch(
        updateSetting({
          id: item.id,
          setting: item.setting,
          value: nextValue,
          visible: item.visible,
        }),
      ).unwrap();
      toast.success(
        `Setting ${nextValue === "on" ? "enabled" : "disabled"} successfully`,
      );
    } catch (err: any) {
      toast.error(err || "Failed to update setting");
    } finally {
      setUpdatingSettingIds((prev) => prev.filter((id) => id !== item.id));
    }
  };

  const handleToggleVisible = async (item: SettingItem) => {
    const nextVisible = item.visible === "on" ? "off" : "on";

    setUpdatingSettingIds((prev) => [...prev, item.id]);

    try {
      await dispatch(
        updateSetting({
          id: item.id,
          setting: item.setting,
          value: item.value,
          visible: nextVisible,
        }),
      ).unwrap();
      toast.success(
        `Setting visibility ${nextVisible === "on" ? "enabled" : "disabled"} successfully`,
      );
    } catch (err: any) {
      toast.error(err || "Failed to update setting visibility");
    } finally {
      setUpdatingSettingIds((prev) => prev.filter((id) => id !== item.id));
    }
  };

  const handleFormChange = (field: keyof SettingPayload, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRefreshAppData = async () => {
    try {
      const message = await dispatch(refreshAppData()).unwrap();
      toast.success(message);
    } catch (err: any) {
      toast.error(err || "Failed to refresh app data");
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const payload = {
      setting: form.setting.trim(),
      value: form.value.trim(),
      visible: form.visible,
    };

    try {
      if (editing) {
        await dispatch(updateSetting({ id: editing.id, ...payload })).unwrap();
        toast.success("Setting updated successfully");
      } else {
        await dispatch(createSetting(payload)).unwrap();
        toast.success("Setting created successfully");
      }

      handleCloseCreateMerchantModal();
    } catch (err: any) {
      toast.error(err || "Failed to save setting");
    }
  };

  return (
    <ExternalContainer>
      <div className="h-screen w-[1030px]">
        <InternalContainer
          headerText="Settings Management"
          headerAction={
            <button
              type="button"
              onClick={handleRefreshAppData}
              disabled={appDataRefreshing}
              className="h-[44px] w-[190px] rounded-lg bg-gradient-to-r from-[#027EC2] to-[#039EF2] px-4 text-sm font-semibold text-white transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {appDataRefreshing ? "Refreshing..." : "Refresh app data"}
            </button>
          }
        >
          <div className="flex flex-col">
            <SettingsSearchFilter
              search={search}
              onSearchChange={setSearch}
              onClick={handleCreateMerchantClick}
            />
            <div className="border border-[#FFFFFF1A]">
              <SettingsTable
                items={filteredItems}
                loading={loading}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleValue={handleToggleValue}
                onToggleVisible={handleToggleVisible}
                updatingSettingIds={updatingSettingIds}
              />
            </div>
            {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
          </div>
        </InternalContainer>
      </div>
      <AddMarchantModal
        isOpen={isCreateMerchantModalOpen}
        onClose={handleCloseCreateMerchantModal}
        form={form}
        saving={saving}
        onChange={handleFormChange}
        onSubmit={handleSubmit}
        isEditing={Boolean(editing)}
      />
    </ExternalContainer>
  );
};

export default Settings;
